interface CustomCanvasRenderingContext2D extends CanvasRenderingContext2D {
  roundRect: (
    x: number,
    y: number,
    w: number,
    h: number,
    radius: number,
  ) => void;
}

const hexToRgba = (hex: string, alpha: number): string => {
  const bigint = Number.parseInt(hex.replace("#", ""), 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const calculateBarData = (
  frequencyData: Uint8Array,
  width: number,
  barWidth: number,
  gap: number,
): number[] => {
  let units = width / (barWidth + gap);
  let step = Math.floor(frequencyData.length / units);

  if (units > frequencyData.length) {
    units = frequencyData.length;
    step = 1;
  }

  const data: number[] = [];

  for (let i = 0; i < units; i++) {
    let sum = 0;

    for (let j = 0; j < step && i * step + j < frequencyData.length; j++) {
      sum += frequencyData[i * step + j];
    }
    data.push(sum / step);
  }
  return data;
};

export const draw = (
  data: number[],
  canvas: HTMLCanvasElement,
  barWidth: number,
  gap: number,
  backgroundColor: string,
  barColor: string,
): void => {
  const amp = canvas.height / 2;

  const ctx = canvas.getContext("2d") as CustomCanvasRenderingContext2D;
  if (!ctx) {
    return;
  }

  ctx.clearRect(0, 0, canvas.width, canvas.height);

  if (backgroundColor !== "transparent") {
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }

  data.forEach((dp, i) => {
    ctx.fillStyle = barColor;

    const x = i * (barWidth + gap);
    const y = amp - dp / 2;
    const w = barWidth;
    const h = dp || 1;

    ctx.beginPath();
    if (ctx.roundRect) {
      // making sure roundRect is supported by the browser
      ctx.roundRect(x, y, w, h, 50);
      ctx.fill();
    } else {
      // fallback for browsers that do not support roundRect
      ctx.fillRect(x, y, w, h);
    }
  });
};

export const drawCircle = (
  data: Uint8Array,
  canvas: HTMLCanvasElement,
  backgroundColor: string,
  circleColor: string,
  minRadius: number, // Minimum radius of the circle
  minRadiusIdle: number, // Minimum radius of the circle
): void => {
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return;
  }

  const width = canvas.width;
  const height = canvas.height;
  const centerX = width / 2;
  const centerY = height / 2;

  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, width, height);

  // Convert Uint8Array to a regular array
  const dataArray = Array.from(data);
  const max = Math.max(...dataArray);
  const min = Math.min(...dataArray);
  const average =
    dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
  const scaledValue = (average - min) / (max - min);

  const currentRadius = scaledValue > 0.02 ? minRadius : minRadiusIdle;

  // Draw the main circle
  ctx.fillStyle = circleColor;
  ctx.beginPath();
  ctx.arc(centerX, centerY, currentRadius, 0, 2 * Math.PI);
  ctx.fill();

  // Draw the waves around the circle
  const numWaves = 4;
  const waveGap = 20;
  const waveOpacity = 1;

  for (let i = 1; i <= numWaves; i++) {
    const dynamicRadius = currentRadius + i * (waveGap * i * scaledValue);
    ctx.strokeStyle = hexToRgba(circleColor, waveOpacity);
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(centerX, centerY, dynamicRadius, 0, 2 * Math.PI);
    ctx.stroke();
  }
};
